import { ref, computed, watch, onUnmounted } from 'vue';

/**
 * One loader for the whole app.
 *
 * A person waiting doesn't care whether they're waiting on auth or on a page's
 * data — they just know they're waiting. So there is exactly one loader
 * component, mounted in App.vue, and everything that loads registers here.
 *
 * Three rules keep it from flashing or flickering:
 *
 *  1. It only clears when EVERY registered thing is ready, not when any one of
 *     them finishes.
 *  2. It waits before appearing at all, so a fast load never takes the screen.
 *  3. Once up, it stays up briefly — an empty set has to stay empty, and the
 *     loader has to have been visible a moment, before it goes away. Auth
 *     finishing and the next page registering are a frame or two apart, and
 *     hiding in that gap is exactly what reads as a flicker.
 */
// Wait this long before showing anything: most loads finish inside it, and a
// full-screen loader that flashes for a moment is worse than a brief pause.
const SHOW_DELAY_MS = 300;

// Once it IS showing, keep it up at least this long. Otherwise a load that
// finishes just past the delay makes it blink.
const MIN_VISIBLE_MS = 500;

// An empty set has to stay empty this long before the loader clears. Auth
// finishing and the next page registering are a frame or two apart, and hiding
// in that window is exactly what reads as a flicker.
const SETTLE_MS = 250;

const pendingKeys = ref(new Set());
const visible = ref(false);

let showTimer = null;
let hideTimer = null;
let shownAt = 0;

const clearTimers = () => {
  if (showTimer) { clearTimeout(showTimer); showTimer = null; }
  if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
};

const hideNow = () => {
  visible.value = false;
  shownAt = 0;
};

watch(
  () => pendingKeys.value.size > 0,
  (busy) => {
    clearTimers();

    if (busy) {
      if (visible.value) return;      // already up; nothing to schedule
      showTimer = setTimeout(() => {
        visible.value = true;
        shownAt = Date.now();
        showTimer = null;
      }, SHOW_DELAY_MS);
      return;
    }

    // Finished before the delay elapsed — it never appeared, so nothing to hide.
    if (!visible.value) return;

    const shownFor = Date.now() - shownAt;
    const wait = Math.max(SETTLE_MS, MIN_VISIBLE_MS - shownFor);
    hideTimer = setTimeout(() => {
      hideNow();
      hideTimer = null;
    }, wait);
  },
  { immediate: true },
);

function setLoading(key, loading) {
  const next = new Set(pendingKeys.value);
  if (loading) {
    next.add(key);
  } else {
    next.delete(key);
  }
  pendingKeys.value = next;
}

/** True while anything is still loading. Read by App.vue's single loader. */
const isAppLoading = computed(() => visible.value);

let keySeq = 0;

/**
 * Register one or more things this page is waiting on. The loader stays up
 * until all of them are ready.
 *
 * @param {string} [name] - label for the key; aids debugging only
 * @returns {{ setPageLoading: (busy: boolean) => void, waitFor: (...sources: Array<() => boolean>) => void }}
 */
export function useAppLoading(name = 'page') {
  const key = `${name}-${++keySeq}`;

  // Navigating away mid-load must not pin the loader open forever.
  onUnmounted(() => setLoading(key, false));

  return {
    isAppLoading,
    setPageLoading: (busy) => setLoading(key, busy),

    /**
     * Watch several readiness getters at once; the loader clears only when all
     * of them report ready.
     *
     * @param {...(() => boolean)} sources - each returns true when ITS part is ready
     */
    waitFor: (...sources) => {
      watch(
        () => sources.every((isReady) => isReady()),
        (allReady) => setLoading(key, !allReady),
        { immediate: true },
      );
    },
  };
}

/** For non-component callers. */
export function setGlobalLoading(key, loading) {
  setLoading(key, loading);
}

export { isAppLoading };
